// Report templates as typed data (no JSX, no imports) so the chat flow, the Reports
// page and tests share one definition. Values in PERIODS/AUDIENCES/TONES must stay
// in sync with supabase/functions/_shared/report-options.ts, which validates them.

export type ReportInputKey = 'period' | 'audience' | 'tone';

export interface ReportInputOption {
  value: string;
  label: string;
}

export interface ReportTemplateInput {
  key: ReportInputKey;
  /** Short question the assistant asks. */
  question: string;
  options: ReportInputOption[];
}

export interface ReportTemplate {
  /** Matches the reportType accepted by the generate-report edge function. */
  id: string;
  name: string;
  description: string;
  /** Lowercase words that suggest this template when found in typed text. */
  keywords: string[];
  inputs: ReportTemplateInput[];
}

export const PERIOD_DAYS: Record<string, number> = {
  'last-7-days': 7,
  'last-30-days': 30,
  'last-quarter': 90,
};

const period = (options: string[]): ReportTemplateInput => ({
  key: 'period',
  question: 'Which period should it cover?',
  options: options.map((value) => ({
    value,
    label: { 'last-7-days': 'Last 7 days', 'last-30-days': 'Last 30 days', 'last-quarter': 'Last quarter' }[value] ?? value,
  })),
});

const audience = (options: Array<[string, string]>): ReportTemplateInput => ({
  key: 'audience',
  question: 'Who is it for?',
  options: options.map(([value, label]) => ({ value, label })),
});

const tone: ReportTemplateInput = {
  key: 'tone',
  question: 'What tone fits?',
  options: [
    { value: 'formal', label: 'Formal' },
    { value: 'concise', label: 'Concise' },
    { value: 'candid', label: 'Candid' },
  ],
};

export const REPORT_TEMPLATES: ReportTemplate[] = [
  {
    id: 'weekly-status',
    name: 'Weekly Status Report',
    description: 'Progress, accomplishments and upcoming tasks',
    keywords: ['weekly', 'status', 'week', 'progress'],
    inputs: [
      period(['last-7-days']),
      audience([['team', 'Project team'], ['stakeholders', 'Stakeholders'], ['executives', 'Executives']]),
    ],
  },
  {
    id: 'monthly-summary',
    name: 'Monthly Summary',
    description: 'KPIs, budget, timeline and team insights',
    keywords: ['monthly', 'month', 'summary', 'kpi', 'budget'],
    inputs: [
      period(['last-30-days', 'last-quarter']),
      audience([['stakeholders', 'Stakeholders'], ['executives', 'Executives'], ['pmo', 'PMO']]),
    ],
  },
  {
    id: 'stakeholder-update',
    name: 'Stakeholder Update',
    description: 'Health, milestones and decisions needed',
    keywords: ['stakeholder', 'update', 'executive', 'steering', 'client'],
    inputs: [
      audience([['executives', 'Executives'], ['client', 'Client'], ['steering-committee', 'Steering committee']]),
      tone,
    ],
  },
  {
    id: 'risk-assessment',
    name: 'Risk Assessment',
    description: 'Risk dashboard, mitigation status and actions',
    keywords: ['risk', 'risks', 'assessment', 'mitigation', 'issue'],
    inputs: [
      period(['last-7-days', 'last-30-days']),
      audience([['pmo', 'PMO'], ['executives', 'Executives'], ['team', 'Project team']]),
    ],
  },
  {
    id: 'team-performance',
    name: 'Team Performance',
    description: 'Productivity, collaboration and recognition',
    keywords: ['team', 'performance', 'productivity', 'velocity'],
    inputs: [
      period(['last-30-days', 'last-quarter']),
      audience([['team-leads', 'Team leads'], ['executives', 'Executives']]),
    ],
  },
];

export function getTemplate(id: string | undefined): ReportTemplate | undefined {
  return REPORT_TEMPLATES.find((t) => t.id === id);
}

/** Best-matching templates for typed text, most keyword hits first (max 3). */
export function suggestTemplates(text: string): ReportTemplate[] {
  const words = new Set(text.toLowerCase().match(/[a-z]+/g) ?? []);
  return REPORT_TEMPLATES
    .map((t) => ({ t, hits: t.keywords.filter((k) => words.has(k)).length }))
    .filter((x) => x.hits > 0)
    .sort((a, b) => b.hits - a.hits)
    .slice(0, 3)
    .map((x) => x.t);
}
